import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  ShieldCheck, ArrowLeft, ArrowRight, Loader2, BarChart3, AlertCircle,
  Activity, Clock, TrendingUp, TrendingDown, Bell, Users, Award, Sparkles,
  CalendarClock, MapPin, Phone, Share2, Copy, Check, Link2,
} from "lucide-react";
import { toast } from "sonner";

// Trial CTAs from the sample report always land on the Haruki-branded /trial intake page
// (NOT the Emergent OAuth screen). Use a relative path — never hardcode origins.
const handleLogin = () => {
  window.location.href = "/trial";
};

const TONE = {
  leaf: "hsl(var(--haruki-leaf))",
  teal: "hsl(var(--haruki-teal))",
  rust: "hsl(var(--haruki-rust))",
  neutral: "hsl(var(--haruki-neutral))",
};

export default function DemoSampleReport() {
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Track view with source attribution
  useEffect(() => {
    const source = params.get("source") || "direct";
    api.post("/demo/sample-report/view", {
      source,
      referrer: typeof document !== "undefined" ? document.referrer || null : null,
    }).catch(() => {}); // best-effort, non-blocking
  }, [params]);

  useEffect(() => {
    api.get("/demo/sample-report")
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Could not load sample report."))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Slim nav */}
      <header className="border-b border-border bg-background/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/demo" data-testid="sample-back-link"
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Back to demo
          </Link>
          <div className="flex items-center gap-2">
            <div className="rounded-md p-1.5" style={{ background: "hsl(var(--haruki-leaf))" }}>
              <ShieldCheck className="h-4 w-4 text-white" />
            </div>
            <span className="font-heading text-base font-bold tracking-tight">Haruki Health</span>
          </div>
          <Button
            data-testid="sample-trial-button"
            onClick={handleLogin}
            size="sm"
            style={{ background: "hsl(var(--haruki-leaf))", color: "white" }}
          >
            Start 21-day trial
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10 lg:py-14">
        {/* Shareable link UI */}
        <ShareLinkPanel currentSource={params.get("source")} />

        {/* Sample banner */}
        <div data-testid="sample-banner"
             className="mb-8 flex flex-wrap items-center justify-between gap-4 rounded-lg border-2 px-5 py-4"
             style={{ borderColor: "hsl(var(--haruki-teal))", background: "hsl(var(--haruki-teal) / 0.08)" }}>
          <div className="flex items-center gap-3">
            <Sparkles className="h-4 w-4" style={{ color: "hsl(var(--haruki-teal))" }} />
            <div>
              <div className="text-xs font-bold uppercase tracking-widest" style={{ color: "hsl(var(--haruki-teal))" }}>
                Sample Report · Mock Data
              </div>
              <div className="text-sm text-muted-foreground">
                This is exactly what your agency console looks like — with sample numbers from a hypothetical 24-member roster.
              </div>
            </div>
          </div>
          <Button
            data-testid="sample-cta-trial"
            onClick={handleLogin}
            size="lg"
            className="text-base font-bold shadow-lg transition-transform hover:scale-[1.03] hover:shadow-xl"
            style={{
              background: "hsl(var(--haruki-leaf))",
              color: "white",
              boxShadow: "0 6px 24px -6px hsl(var(--haruki-leaf) / 0.55)",
            }}
          >
            Start your own trial <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>

        <div className="mb-1 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
          Agency Console · Sample Report
        </div>
        <h1 className="font-heading text-3xl font-bold lg:text-4xl">
          What HR, command staff & training directors see.
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          The Haruki agency console surfaces participation metrics only — never scenario content, never individual
          scores. Below: the engagement view rendered against a sample dataset.
        </p>

        {loading && (
          <div className="mt-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Loading sample…
          </div>
        )}

        {error && (
          <div data-testid="sample-error" className="mt-10 rounded-md border p-6"
               style={{ borderColor: "hsl(var(--haruki-fire))", background: "hsl(var(--haruki-fire) / 0.06)" }}>
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5" style={{ color: "hsl(var(--haruki-fire))" }} />
              <div>{error}</div>
            </div>
          </div>
        )}

        {data && (
          <>
            {/* Trial banner replica */}
            <div className="mt-8 flex flex-wrap items-center justify-between gap-3 rounded-md border px-4 py-3"
                 style={{ borderColor: "hsl(var(--haruki-leaf))", background: "hsl(var(--haruki-leaf) / 0.08)" }}>
              <div className="flex items-center gap-3">
                <CalendarClock className="h-4 w-4" style={{ color: "hsl(var(--haruki-leaf-deep))" }} />
                <div>
                  <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: "hsl(var(--haruki-leaf-deep))" }}>
                    21-day free trial
                  </div>
                  <div className="text-sm">
                    {data.trial_days_remaining} days remaining. Full access to all four training modules.
                  </div>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">{data.agency_name}</div>
            </div>

            {/* Top stats */}
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Stat icon={Users} label="Roster" value={data.roster_size}
                    subline="Service members enrolled" tone="leaf" test="sample-stat-roster" />
              <Stat icon={Activity} label="Active (30d)"
                    value={`${data.active_users_30d}/${data.roster_size}`}
                    subline={`${Math.round(data.active_rate * 100)}% of roster active`} tone="leaf"
                    test="sample-stat-active" />
              <Stat icon={TrendingUp} label="Completions (30d)" value={data.completions_recent}
                    subline={`+${Math.round(data.completions_delta * 100)}% vs. prior period`} tone="teal"
                    test="sample-stat-completions" />
              <Stat icon={Clock} label="Days to 1st scenario" value={data.avg_days_to_first_scenario}
                    subline="Onboarding friction (lower is better)" tone="teal"
                    test="sample-stat-ttf" />
            </div>

            <div className="mt-10 grid gap-6 lg:grid-cols-3">
              {/* Daily completions */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="font-heading text-base">Daily completions · last 30 days</CardTitle>
                </CardHeader>
                <CardContent>
                  <DailyBars data={data.daily_completions_30d} color="hsl(var(--haruki-leaf))" />
                  <p className="mt-3 text-xs text-muted-foreground">
                    Useful for spotting operational dips (high-load shifts, leave) and timing command pushes.
                  </p>
                </CardContent>
              </Card>

              {/* Hour heatmap */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading text-base">When do they train?</CardTitle>
                </CardHeader>
                <CardContent>
                  <HourHeatmap data={data.hour_heatmap} />
                  <p className="mt-3 text-xs text-muted-foreground">Heatmap helps with timing critical-incident pushes.</p>
                </CardContent>
              </Card>

              {/* Module completion */}
              <Card className="lg:col-span-3">
                <CardHeader>
                  <CardTitle className="font-heading text-base">Module completion</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 sm:grid-cols-3">
                    {Object.entries(data.module_stats).map(([k, v]) => {
                      const titleMap = { new_hire: "New Hire", annual: "Annual", critical_incident: "Critical Incident" };
                      const rate = Math.round(v.completion_rate * 100);
                      return (
                        <div key={k} data-testid={`sample-module-${k}`}
                             className="rounded-md border border-border p-4">
                          <div className="flex items-center justify-between">
                            <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                              {titleMap[k] || k}
                            </div>
                            <div className="font-heading text-2xl font-extrabold tabular-nums"
                                 style={{ color: "hsl(var(--haruki-leaf-deep))" }}>{rate}%</div>
                          </div>
                          <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full"
                               style={{ background: "hsl(var(--secondary))" }}>
                            <div className="h-full rounded-full"
                                 style={{ width: `${rate}%`, background: "hsl(var(--haruki-leaf))" }} />
                          </div>
                          <div className="mt-3 text-xs text-muted-foreground">
                            {v.completed} of {v.enrolled} {k === "critical_incident" ? "pushed" : "enrolled"}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* HMDE distribution */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="font-heading text-base">Aggregate HMDE zone distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <ZoneBars dist={data.hmde_distribution} trends={data.hmde_trends} />
                <p className="mt-3 text-xs text-muted-foreground">
                  Aggregate-only. Individual zones and numeric scores are never visible to admins — even in real data.
                  Cohort {data.hmde_distribution.cohort_size} ≥ {data.hmde_distribution.min_cohort_required} required to display.
                </p>
              </CardContent>
            </Card>

            {/* SOS + Leads + CEU */}
            <div className="mt-6 grid gap-6 lg:grid-cols-3">
              <Card data-testid="sample-sos-card">
                <CardHeader><CardTitle className="font-heading text-base">SOS signals · 30 days</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div className="font-heading text-3xl font-extrabold" style={{ color: "hsl(var(--haruki-rust))" }}>
                    {data.sos_recent} <span className="text-sm font-medium text-muted-foreground">vs. {data.sos_prior} prior</span>
                  </div>
                  <ul className="space-y-2 text-xs text-muted-foreground">
                    {data.sos_signals.map((s, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <Bell className="h-3 w-3" style={{ color: "hsl(var(--haruki-teal))" }} />
                        <span>{s.triggered_at.slice(0, 10)}</span>
                        <MapPin className="h-3 w-3" /><span>{s.region}</span>
                        <Phone className="h-3 w-3" /><span>{s.eap_phone}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-[10px] text-muted-foreground">
                    Admin sees only timestamp + geo resources. Never who triggered, never the conversation.
                  </p>
                </CardContent>
              </Card>

              <Card data-testid="sample-ceu-card">
                <CardHeader><CardTitle className="font-heading text-base">CEU certificates issued</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex items-center gap-3">
                    <Award className="h-6 w-6" style={{ color: "hsl(var(--haruki-leaf))" }} />
                    <div className="font-heading text-3xl font-extrabold tabular-nums">{data.ceus_issued}</div>
                  </div>
                  <p className="mt-3 text-xs text-muted-foreground">
                    PDF certificates, downloadable by each service member. Usable toward POST / accreditation /
                    HR records.
                  </p>
                </CardContent>
              </Card>

              <Card data-testid="sample-leads-card">
                <CardHeader><CardTitle className="font-heading text-base">Lead pipeline</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs text-muted-foreground">Total leads</div>
                      <div className="font-heading text-2xl font-extrabold tabular-nums">{data.lead_analytics.total}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Conversion</div>
                      <div className="font-heading text-2xl font-extrabold tabular-nums"
                           style={{ color: "hsl(var(--haruki-leaf-deep))" }}>
                        {Math.round(data.lead_analytics.conversion_rate * 100)}%
                      </div>
                    </div>
                  </div>
                  <p className="mt-3 text-xs text-muted-foreground">
                    {data.lead_analytics.last_7d} new in last 7 days · {data.lead_analytics.converted} converted to trials.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Vertical breakdown */}
            <Card className="mt-6">
              <CardHeader><CardTitle className="font-heading text-base">Roster by vertical</CardTitle></CardHeader>
              <CardContent>
                <div className="grid gap-3 sm:grid-cols-4">
                  {Object.entries(data.vertical_breakdown).map(([k, v]) => (
                    <div key={k} className="rounded-md border border-border p-4">
                      <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{k}</div>
                      <div className="mt-2 font-heading text-2xl font-extrabold tabular-nums">{v}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Confidentiality reminder */}
            <div className="mt-8 flex items-start gap-3 rounded-md border border-border p-4 text-xs text-muted-foreground">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>{data.confidentiality_notice}</div>
            </div>

            {/* Final CTA */}
            <div className="mt-10 rounded-xl border p-10 text-center"
                 style={{ background: "hsl(var(--haruki-leaf-deep))", color: "white" }}>
              <BarChart3 className="mx-auto h-7 w-7" style={{ color: "hsl(var(--haruki-leaf))" }} />
              <h2 className="mt-3 font-heading text-3xl font-bold">This — but with your agency's data.</h2>
              <p className="mx-auto mt-3 max-w-xl text-sm text-white/80">
                Spin up a 21-day trial in under two minutes. Sign in with Google, set your vertical, push New Hire
                training to your roster, and the dashboard above starts populating with real numbers.
              </p>
              <Button
                data-testid="sample-final-trial-button"
                onClick={handleLogin}
                size="lg"
                className="mt-6"
                style={{ background: "hsl(var(--haruki-leaf))", color: "white" }}
              >
                Start 21-day trial <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </>
        )}
      </main>

      {/* Sticky floating CTA — visible at all times on the long report */}
      <button
        data-testid="sample-floating-cta"
        onClick={handleLogin}
        className="fixed bottom-6 right-6 z-50 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold uppercase tracking-wider text-white shadow-2xl transition-transform hover:scale-105"
        style={{
          background: "hsl(var(--haruki-leaf))",
          boxShadow: "0 10px 32px -8px hsl(var(--haruki-leaf) / 0.6), 0 0 0 4px hsl(var(--haruki-leaf) / 0.12)",
        }}
      >
        Start 21-day trial <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, subline, tone = "leaf", test }) => (
  <Card data-testid={test}>
    <CardContent className="p-5">
      <div className="flex items-center gap-2">
        <div className="rounded-md p-1.5" style={{ background: TONE[tone], color: "white" }}>
          <Icon className="h-3.5 w-3.5" />
        </div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
      <div className="mt-3 font-heading text-2xl font-extrabold tabular-nums">{value}</div>
      <div className="mt-1 text-xs text-muted-foreground">{subline}</div>
    </CardContent>
  </Card>
);

const DailyBars = ({ data = [], color = "hsl(var(--haruki-leaf))" }) => {
  const max = Math.max(1, ...data);
  return (
    <div className="flex h-40 items-end gap-1">
      {data.map((v, i) => (
        <div key={i} className="flex-1">
          <div
            className="w-full rounded-t transition-opacity hover:opacity-80"
            style={{ height: `${Math.max(Math.round((v / max) * 100), 4)}%`, background: v > 0 ? color : "hsl(var(--border))" }}
            title={`Day ${i + 1}: ${v}`}
          />
        </div>
      ))}
    </div>
  );
};

const HourHeatmap = ({ data = [] }) => {
  const max = Math.max(1, ...data);
  return (
    <div className="grid grid-cols-12 gap-1">
      {data.map((v, h) => {
        const alpha = v === 0 ? 0.08 : 0.15 + (v / max) * 0.85;
        return (
          <div key={h} className="aspect-square rounded"
               style={{ background: `hsl(var(--haruki-leaf) / ${alpha})` }}
               title={`${h}:00 — ${v}`} />
        );
      })}
    </div>
  );
};

const ZoneBars = ({ dist, trends }) => {
  const total = Math.max(dist.total, 1);
  const rows = [
    { key: "resilience", label: "Resilience Zone", color: "hsl(var(--haruki-leaf))" },
    { key: "neutral",    label: "Neutral Zone",    color: "hsl(var(--haruki-neutral))" },
    { key: "fire",       label: "Emotional Fire Zone", color: "hsl(var(--haruki-fire))" },
  ];
  return (
    <div className="space-y-4">
      {rows.map(r => {
        const v = dist[r.key] || 0;
        const pct = Math.round((v / total) * 100);
        return (
          <div key={r.key}>
            <div className="mb-2 flex items-center justify-between text-xs">
              <span className="font-semibold uppercase tracking-widest" style={{ color: r.color }}>{r.label}</span>
              <span className="text-muted-foreground">{v} response{v === 1 ? "" : "s"} ({pct}%)</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
              <div className="h-full rounded-full" style={{ width: `${pct}%`, background: r.color }} />
            </div>
          </div>
        );
      })}
      <div className="grid gap-3 pt-4 sm:grid-cols-3">
        {["30d", "90d", "365d"].map(w => (
          <div key={w} className="rounded-md border border-border p-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{w} Fire trend</div>
            <div className="mt-1 text-sm font-semibold capitalize">{trends?.[w]?.direction || "stable"}</div>
          </div>
        ))}
      </div>
    </div>
  );
};


const PRESET_SOURCES = [
  { key: "linkedin", label: "LinkedIn" },
  { key: "twitter", label: "X / Twitter" },
  { key: "email", label: "Email" },
  { key: "newsletter", label: "Newsletter" },
  { key: "conference", label: "Conference" },
  { key: "podcast", label: "Podcast" },
  { key: "sales", label: "Sales outreach" },
  { key: "iacp", label: "IACP" },
  { key: "iafc", label: "IAFC" },
  { key: "napsg", label: "NAPSG" },
];

const ShareLinkPanel = ({ currentSource }) => {
  const [source, setSource] = useState(currentSource || "linkedin");
  const [custom, setCustom] = useState("");
  const [copied, setCopied] = useState(false);

  const buildUrl = (src) => {
    const base = typeof window !== "undefined"
      ? `${window.location.origin}/demo/sample-report`
      : "/demo/sample-report";
    const s = (src || "").trim();
    return s ? `${base}?source=${encodeURIComponent(s)}` : base;
  };

  const effectiveSource = custom.trim() || source;
  const url = buildUrl(effectiveSource);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(`Copied: ${effectiveSource ? `?source=${effectiveSource}` : "no source"}`);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy to clipboard.");
    }
  };

  return (
    <div data-testid="share-link-panel"
         className="mb-6 rounded-md border border-border p-4"
         style={{ background: "hsl(var(--card))" }}>
      <div className="flex flex-wrap items-center gap-2">
        <Share2 className="h-4 w-4" style={{ color: "hsl(var(--haruki-teal))" }} />
        <div className="text-xs font-bold uppercase tracking-widest" style={{ color: "hsl(var(--haruki-teal))" }}>
          Shareable link
        </div>
        {currentSource && (
          <span data-testid="share-current-source"
                className="rounded-full px-2 py-0.5 text-[10px] font-semibold"
                style={{ background: "hsl(var(--haruki-leaf) / 0.15)", color: "hsl(var(--haruki-leaf-deep))" }}>
            Viewed via: {currentSource}
          </span>
        )}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {PRESET_SOURCES.map((p) => (
          <button
            key={p.key}
            data-testid={`share-preset-${p.key}`}
            onClick={() => { setSource(p.key); setCustom(""); }}
            className="rounded-full border px-3 py-1 text-xs font-medium transition-colors"
            style={{
              borderColor: source === p.key && !custom.trim() ? "hsl(var(--haruki-leaf))" : "hsl(var(--border))",
              background: source === p.key && !custom.trim() ? "hsl(var(--haruki-leaf) / 0.1)" : "transparent",
              color: source === p.key && !custom.trim() ? "hsl(var(--haruki-leaf-deep))" : "hsl(var(--foreground))",
            }}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <input
          data-testid="share-custom-input"
          type="text"
          value={custom}
          onChange={(e) => setCustom(e.target.value.replace(/[^a-z0-9_\-]/gi, "_").toLowerCase())}
          placeholder="Or custom source (e.g. sept_breakfast)"
          className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground/40"
        />
        <div className="flex flex-1 items-center gap-2 rounded-md border border-border bg-secondary/40 px-3 py-2">
          <Link2 className="h-4 w-4 shrink-0 text-muted-foreground" />
          <code data-testid="share-url-preview" className="flex-1 truncate text-xs">{url}</code>
        </div>
        <Button
          data-testid="share-copy-button"
          onClick={copy}
          style={{ background: "hsl(var(--haruki-leaf))", color: "white" }}
        >
          {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
          {copied ? "Copied" : "Copy link"}
        </Button>
      </div>
      <div className="mt-3 text-[11px] text-muted-foreground">
        Each click on the URL increments a per-source counter. View totals on the agency console → Lead Analytics.
      </div>
    </div>
  );
};
